import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Button } from '@shared/components/ui';
import { StatusChip } from '@shared/components/StatusChip';
import { getStatusTone } from '@shared/config/theme';
import {
  Receipt,
  Download,
  Calendar,
  DollarSign,
  RotateCcw,
  CreditCard,
  CheckCircle,
  Filter,
  X,
} from 'lucide-react';

interface Invoice {
  id: string;
  organizationId: string;
  stripeInvoiceId?: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string;
  dueDate?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
  paymentMethod?: {
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
  };
}

interface InvoicesResponse {
  invoices: Invoice[];
  total: number;
  limit: number;
  offset: number;
}

export default function InvoicesPage() {
  const { t, i18n } = useTranslation('billing');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const { showError } = useDialog();
  const hasActiveFilters =
    statusFilter !== 'all' || dateFilter !== 'all' || startDate !== '' || endDate !== '';

  const {
    data: invoicesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['invoices', statusFilter, dateFilter, startDate, endDate],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const now = new Date();
      if (dateFilter === '30days' || dateFilter === '90days') {
        const days = dateFilter === '30days' ? 30 : 90;
        const start = new Date(now);
        start.setDate(start.getDate() - days);
        params.set('start_date', start.toISOString());
        params.set('end_date', now.toISOString());
      } else if (dateFilter === 'custom') {
        if (startDate) params.set('start_date', new Date(`${startDate}T00:00:00`).toISOString());
        if (endDate) params.set('end_date', new Date(`${endDate}T23:59:59`).toISOString());
      }
      const query = params.size ? `?${params.toString()}` : '';
      const response = await fetch(`/api/v1/billing/invoices${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(t('errors.fetchFailed'));
      const data = await response.json();
      return {
        ...data,
        invoices: Array.isArray(data?.invoices) ? data.invoices : [],
      } as InvoicesResponse;
    },
  });

  const handleDownload = async (invoiceId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/billing/invoices/${invoiceId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate download link');
      const data = await response.json();
      if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      await showError('Error', t('errors.downloadFailed'));
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };
  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, { dateStyle: 'medium' }).format(
      new Date(date)
    );
  const getStatusLabel = (status: string) =>
    t(`invoices.status.${status.toLowerCase()}`, { defaultValue: status });

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
              {t('invoices.title')}
            </h1>
            <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark sm:text-base">
              {t('invoices_page.billing_history_and_payment_records')}
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label htmlFor="invoice-status-filter" className="app-field-label">
              {t('invoices.columns.status')}
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4" />
              <select
                id="invoice-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="app-field-control w-full pl-10 pr-4"
              >
                <option value="all">{t('invoices_page.all_statuses')}</option>
                <option value="paid">{t('invoices_page.paid')}</option>
                <option value="open">{t('invoices.status.open')}</option>
                <option value="void">{t('invoices_page.void')}</option>
                <option value="uncollectible">{t('invoices_page.uncollectible')}</option>
              </select>
            </div>
          </div>

          <div className="flex-1 w-full">
            <label htmlFor="invoice-date-filter" className="app-field-label">
              {t('invoices.dateRange')}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4" />
              <select
                id="invoice-date-filter"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="app-field-control w-full pl-10 pr-4"
              >
                <option value="all">{t('invoices_page.all_time')}</option>
                <option value="30days">{t('invoices_page.last_30_days')}</option>
                <option value="90days">{t('invoices_page.last_90_days')}</option>
                <option value="custom">{t('invoices_page.custom_range')}</option>
              </select>
            </div>
          </div>

          {dateFilter === 'custom' && (
            <>
              <div className="flex-1 w-full">
                <label htmlFor="invoice-start-date" className="app-field-label">
                  {t('invoices.startDate')}
                </label>
                <input
                  id="invoice-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="app-field-control w-full"
                  max={endDate || undefined}
                />
              </div>
              <div className="flex-1 w-full">
                <label htmlFor="invoice-end-date" className="app-field-label">
                  {t('invoices.endDate')}
                </label>
                <input
                  id="invoice-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="app-field-control w-full"
                  min={startDate || undefined}
                />
              </div>
            </>
          )}

          <Button
            variant="secondary"
            aria-label={t('invoices.resetFilters')}
            title={t('invoices.resetFilters')}
            className="min-h-11"
            onClick={() => {
              setStatusFilter('all');
              setDateFilter('all');
              setStartDate('');
              setEndDate('');
            }}
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </Card>

        {/* Invoice List */}
        {isLoading ? (
          <div className="py-20 flex justify-center">
            <div
              role="status"
              aria-label={t('invoices.loading')}
              className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"
            ></div>
          </div>
        ) : error ? (
          <Card className="space-y-4 py-12 text-center">
            <p
              role="alert"
              className="text-sm font-semibold text-state-danger-light dark:text-state-danger-dark"
            >
              {t('errors.fetchFailed')}
            </p>
            <Button type="button" variant="secondary" onClick={() => void refetch()}>
              {t('invoices.retry')}
            </Button>
          </Card>
        ) : (invoicesData?.invoices.length ?? 0) === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <Receipt
              size={48}
              className="mx-auto text-surface-border-light dark:text-surface-border-dark mb-4"
            />
            <h3 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark">
              {t('invoices_page.no_invoices_found')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
              {hasActiveFilters
                ? t('invoices.filteredEmptyDescription')
                : t('invoices.emptyDescription')}
            </p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {invoicesData?.invoices.map((invoice) => (
              <Card
                key={invoice.id}
                className="p-4 sm:p-5 group transition-all hover:border-brand-200"
              >
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                      <DollarSign size={24} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-black text-lg text-slate-900 dark:text-white">
                          {formatAmount(invoice.amountDue, invoice.currency)}
                        </h3>
                        <StatusChip
                          tone={getStatusTone(invoice.status)}
                          label={getStatusLabel(invoice.status)}
                        />
                      </div>
                      <p className="break-all text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest font-mono">
                        {invoice.stripeInvoiceId || invoice.id.slice(0, 8)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium xl:flex-1 xl:justify-center">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      {formatDate(invoice.createdAt)}
                    </div>
                    {invoice.paidAt && (
                      <div className="flex items-center gap-2 text-state-ok-light dark:text-state-ok-dark">
                        <CheckCircle aria-hidden="true" size={16} />
                        {t('invoices.paidAt', { date: formatDate(invoice.paidAt) })}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row xl:shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('invoices.viewDetails')}
                      onClick={() => setSelectedInvoice(invoice)}
                    >
                      {t('invoices.viewDetails')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void handleDownload(invoice.id)}
                    >
                      <Download aria-hidden="true" size={16} /> {t('invoices.download')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Invoice Details Modal */}
        {selectedInvoice && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm animate-in fade-in duration-300">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="invoice-details-title"
              className="w-full max-w-lg"
            >
              <Card className="w-full shadow-elevated">
                <div className="space-y-6 p-5 sm:p-8">
                  <div className="flex justify-between items-center">
                    <h2
                      id="invoice-details-title"
                      className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark sm:text-2xl"
                    >
                      {t('invoices.detailsTitle')}
                    </h2>
                    <button
                      onClick={() => setSelectedInvoice(null)}
                      type="button"
                      aria-label={t('invoices.close')}
                      className="flex h-11 w-11 items-center justify-center rounded-lg text-text-subtle-light hover:bg-surface-2-light hover:text-slate-900 dark:text-text-subtle-dark dark:hover:bg-surface-2-dark dark:hover:text-white"
                    >
                      <X aria-hidden="true" size={20} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <DetailRow
                      label={t('invoices.amountDue')}
                      value={formatAmount(selectedInvoice.amountDue, selectedInvoice.currency)}
                      large
                    />
                    <DetailRow
                      label={t('invoices.amountPaid')}
                      value={formatAmount(selectedInvoice.amountPaid, selectedInvoice.currency)}
                    />
                    <DetailRow
                      label={t('invoices.columns.status')}
                      value={
                        <StatusChip
                          tone={getStatusTone(selectedInvoice.status)}
                          label={getStatusLabel(selectedInvoice.status)}
                        />
                      }
                    />
                    <DetailRow
                      label={t('invoices.invoiceId')}
                      value={selectedInvoice.stripeInvoiceId || selectedInvoice.id}
                      mono
                    />
                    <DetailRow
                      label={t('invoices.columns.date')}
                      value={formatDate(selectedInvoice.createdAt)}
                    />
                    {selectedInvoice.dueDate && (
                      <DetailRow
                        label={t('invoices.dueDate')}
                        value={formatDate(selectedInvoice.dueDate)}
                      />
                    )}
                    {selectedInvoice.paidAt && (
                      <DetailRow
                        label={t('invoices.paidAtLabel')}
                        value={formatDate(selectedInvoice.paidAt)}
                      />
                    )}

                    {selectedInvoice.paymentMethod && (
                      <div className="pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                        <p className="text-xs font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-2">
                          {t('invoices.paymentMethod')}
                        </p>
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                          <CreditCard aria-hidden="true" size={16} />
                          <span className="capitalize">{selectedInvoice.paymentMethod.brand}</span>
                          <span>•••• {selectedInvoice.paymentMethod.last4}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setSelectedInvoice(null)}
                    >
                      {t('invoices.close')}
                    </Button>
                    <Button type="button" onClick={() => void handleDownload(selectedInvoice.id)}>
                      <Download aria-hidden="true" size={16} /> {t('invoices.download')}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

interface DetailRowProps {
  label: string;
  value: ReactNode;
  mono?: boolean;
  large?: boolean;
}

function DetailRow({ label, value, mono, large }: DetailRowProps) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-surface-border-light dark:border-surface-border-dark last:border-0">
      <span className="text-sm font-bold text-text-muted-light dark:text-text-muted-dark">
        {label}
      </span>
      <span
        className={`text-slate-900 dark:text-white ${mono ? 'font-mono text-xs' : 'font-medium'} ${large ? 'text-xl font-black' : 'text-sm'}`}
      >
        {value}
      </span>
    </div>
  );
}
